import {
  CLIENT_LIFECYCLE,
  listSearch,
  LIFECYCLE_LABEL,
  PLACEMENT_LABEL,
  PLACEMENT_STATUSES,
  type ClientRow,
  type ClientsSummary,
} from '@cyberscore/contracts';
import { Building2, Download, Plus, Upload } from 'lucide-react';
import { useState } from 'react';
import { ApiError, apiBlob } from '../../lib/api';
import { useListState, usePage, type ListSpec } from '../../lib/list';
import { href } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { DataView, type Column, type FilterDef } from '../../ui/DataView';
import { industryLabel } from '../assessments/labels';
import { pct } from '../portfolio/format';
import { ClientQuickView, LifecyclePill, money, NewClientDrawer, RenewalText } from './ClientRecord';
import {
  BAND_FILTER,
  ExceptionsCell,
  INDUSTRY_FILTER,
  ProgressCell,
  ScoreBlock,
  ScoreCell,
  STATUS_FILTER,
  StatusPill,
  when,
} from './parts';
import { ImportClients } from './ImportClients';
import { SavedViews } from './SavedViews';
import { BookAsk } from '../assistant/BookAsk';
import { save } from '../evidence/api';
import { useToast } from '../../ui/Toasts';
import { PlacementPill } from '../insurers/InsurersPage';

const SPEC: ListSpec = {
  id: 'clients',
  sort: 'updated',
  view: 'cards',
  filters: ['tab', 'lifecycle', 'renewing', 'status', 'band', 'industry', 'attention', 'placement'],
};
const ATTENTION: FilterDef = { key: 'attention', label: 'Attention', options: [['to_review', 'To review'], ['exceptions', 'With exceptions'], ['provisional', 'Provisional score'], ['unscored', 'Not scored yet']] }; // prettier-ignore

/**
 * Ready-made views over the book (task 109, decision 0074), so the usual questions are one click rather than
 * a combination of filters: what waits on review, what renews soon, everything, the archive.
 */
const VIEWS = [
  { key: 'all', label: 'All', filters: { tab: '', attention: '', renewing: '' } },
  { key: 'review', label: 'To review', filters: { tab: '', attention: 'to_review', renewing: '' } },
  { key: 'renewing', label: 'Renewing soon', filters: { tab: '', attention: '', renewing: '90' } },
  {
    key: 'archived',
    label: 'Archived',
    filters: { tab: 'archived', attention: '', renewing: '', lifecycle: '' },
  },
] as const;
const STAGE: FilterDef = { key: 'lifecycle', label: 'Stages', options: CLIENT_LIFECYCLE.filter((s) => s !== 'lapsed').map((s) => [s, LIFECYCLE_LABEL[s]] as const) }; // prettier-ignore
const PLACEMENT: FilterDef = { key: 'placement', label: 'With an insurer', options: PLACEMENT_STATUSES.map((s) => [s, PLACEMENT_LABEL[s]] as const) }; // prettier-ignore
const RENEWING: FilterDef = { key: 'renewing', label: 'Renewals', options: [['overdue', 'Renewal passed'], ['30', 'Renewing within 30 days'], ['60', 'Within 60 days'], ['90', 'Within 90 days']] }; // prettier-ignore

/**
 * Clients (tasks 60 and 66): the businesses this tenant works with, as cards (or a table) with each one's
 * stage, policy, latest score and how far its assessment has got. Prospects show before their first
 * assessment; lapsed clients sit in the archive. A click opens the client's quick view.
 */
export function ClientsPage() {
  const list = useListState(SPEC);
  const page = usePage<ClientRow, { summary: ClientsSummary }>('/clients', list.state);
  const { tenant } = useSession();
  const canAdd = can(tenant, 'clients.create');
  const canImport = can(tenant, 'clients.import');
  const canAsk = can(tenant, 'assistant.book');
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const toast = useToast();
  // The rows the list shows, every page, in its order (task 67).
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { q, sort, dir, filters } = list.state;
      save(await apiBlob(`/clients/export${listSearch({ q: q.trim(), sort, dir, ...filters })}`), `clients-${new Date().toISOString().slice(0, 10)}.csv`); // prettier-ignore
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not export', 'bad');
    } finally {
      setExporting(false);
    }
  };
  const s = page.data?.summary;
  const archived = list.state.filters.tab === 'archived';
  // The tab isn't a filter to clear: searching the archive is still the archive.
  const view = {
    ...list,
    filtered: !!list.state.q.trim() || Object.entries(list.state.filters).some(([k, v]) => k !== 'tab' && v),
    clear: () => list.set({ q: '', filters: Object.fromEntries(Object.keys(list.state.filters).map((k) => [k, k === 'tab' ? list.state.filters.tab! : ''])) }), // prettier-ignore
  };
  const name = (c: ClientRow) => (
    <a className="cell-title" href={href.client(c.customer_key)} onClick={(e) => e.stopPropagation()}>{c.customer_name}</a>
  ); // prettier-ignore

  const columns: Column<ClientRow>[] = [
    { key: 'name', label: 'Client', sort: 'name', render: (c) => (<>{name(c)}<div className="muted small">{c.latest?.title ?? c.policy_number ?? 'No assessment yet'}</div></>) }, // prettier-ignore
    { key: 'stage', label: 'Stage', render: (c) => <LifecyclePill stage={c.lifecycle} /> },
    {
      key: 'score',
      label: 'CyberScore',
      sort: 'score',
      render: (c) => <ScoreCell s={c.latest?.score ?? null} />,
    },
    {
      key: 'progress',
      label: 'Progress',
      sort: 'progress',
      wide: true,
      render: (c) => <ProgressCell s={c.latest?.score ?? null} />,
    },
    {
      key: 'exceptions',
      label: 'Exceptions',
      wide: true,
      render: (c) => <ExceptionsCell s={c.latest?.score ?? null} />,
    },
    { key: 'insurers', label: 'Insurers', wide: true, render: (c) => <Placements c={c} /> },
    { key: 'renewal', label: 'Renewal', sort: 'renewal', wide: true, render: (c) => <RenewalText date={c.renewal_date} /> }, // prettier-ignore
    { key: 'premium', label: 'Premium', sort: 'premium', wide: true, align: 'right', render: (c) => <span className="num">{money(c.premium)}</span> }, // prettier-ignore
    { key: 'assessments', label: 'Assessments', sort: 'assessments', align: 'right', render: (c) => <span className="num">{c.assessments}{c.in_progress ? <span className="muted small"> · {c.in_progress} open</span> : null}</span> }, // prettier-ignore
    { key: 'status', label: 'Latest', render: (c) => (c.latest ? <StatusPill status={c.latest.status} /> : <span className="muted">–</span>) }, // prettier-ignore
    {
      key: 'updated',
      label: 'Updated',
      sort: 'updated',
      wide: true,
      render: (c) => <span className="muted">{when(c.updated_at)}</span>,
    },
  ];

  return (
    <>
      <div className="page-head">
        <h1>Clients</h1>
        <p>The businesses you work with: where each stands, its policy, the latest CyberScore and how far its assessment has got.</p>
      </div>
      {s && (
        <div className="stat-grid">
          {([
            ['Clients', String(s.clients), `${s.assessments} assessments${s.renewing ? ` · ${s.renewing} renewing within 90 days` : ''}`],
            ['In progress', String(s.in_progress), 'assessments still being answered'],
            ['Average CyberScore', s.average_score === null ? '–' : String(s.average_score), `latest per client${s.provisional ? `, ${s.provisional} provisional` : ''}`],
            ['With exceptions', String(s.with_exceptions), s.clients ? `${pct(s.with_exceptions / s.clients)} of clients` : 'kill switches not in place'],
          ] as const).map(([label, value, foot]) => (
            <section key={label} className="card stat">
              <div className="eyebrow">{label}</div>
              <div className="stat-val">{value}</div>
              <div className="muted small">{foot}</div>
            </section>
          ))}
        </div>
      )}
      {canAsk && <BookAsk onOpen={setOpen} />}
      <div className="seg list-tabs" role="group" aria-label="Which clients">
        {VIEWS.map((v) => {
          const f = list.state.filters;
          const on = (f.tab ?? '') === v.filters.tab && (f.attention ?? '') === v.filters.attention && (f.renewing ?? '') === v.filters.renewing;
          const n = !s ? null : v.key === 'all' ? s.clients : v.key === 'review' ? s.to_review : v.key === 'renewing' ? s.renewing_soon : s.archived;
          return (
            <button key={v.key} type="button" aria-pressed={on} onClick={() => list.set({ filters: v.filters })}>
              {v.label}{n !== null ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>
      <DataView
        list={view}
        query={page}
        noun="clients"
        rowKey={(c) => c.customer_key}
        onRow={(c) => setOpen(c.customer_key)}
        columns={columns}
        card={(c) => <ClientCard c={c} name={name(c)} />}
        filters={[...(archived ? [] : [STAGE]), RENEWING, STATUS_FILTER, BAND_FILTER, INDUSTRY_FILTER, ATTENTION, PLACEMENT]}
        sorts={[
          { value: 'updated', label: 'Recent activity', dir: 'desc' },
          { value: 'name', label: 'Name', dir: 'asc' },
          { value: 'renewal', label: 'Renewal date', dir: 'asc' },
          { value: 'premium', label: 'Premium', dir: 'desc' },
          { value: 'score', label: 'CyberScore', dir: 'asc' },
          { value: 'progress', label: 'Progress', dir: 'asc' },
          { value: 'assessments', label: 'Assessments', dir: 'desc' },
        ]}
        searchLabel="Search clients or policy numbers"
        foldFilters
        actions={
          <>
            <SavedViews list="clients" base={href.clients()} />
            <button type="button" className="btn" onClick={exportCsv} disabled={exporting} title="The clients this list shows, as CSV"><Download size={15} /> Export</button>
            {canImport && <button type="button" className="btn" onClick={() => setImporting(true)}><Upload size={15} /> Import</button>}
            {canAdd && <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}><Plus size={15} /> New client</button>}
          </>
        }
        empty={archived
          ? <><Building2 size={28} /><strong>No lapsed clients</strong>Clients whose stage is Lapsed move here.</>
          : <><Building2 size={28} /><strong>No clients yet</strong>Add a client, or start an assessment for a business, and it appears here.</>}
      />
      {open && <ClientQuickView customerKey={open} onClose={() => setOpen(null)} />}
      {importing && <ImportClients onClose={() => setImporting(false)} />}
      {adding && <NewClientDrawer onClose={() => setAdding(false)} onCreated={(k) => { setAdding(false); setOpen(k); }} />}
    </>
  ); // prettier-ignore
}

function ClientCard({ c, name }: { c: ClientRow; name: React.ReactNode }) {
  const s = c.latest?.score ?? null;
  return (
    <article className="ccard">
      <div className="ccard-head">
        <div className="minw">
          <h3>{name}</h3>
          <div className="muted small">{industryLabel(c.industry)}{c.country ? ` · ${c.country}` : ''}</div>
        </div>
        <LifecyclePill stage={c.lifecycle} />
      </div>
      <ScoreBlock s={s} />
      {(c.policy_number || c.renewal_date || c.premium !== null) && (
        <div className="ccard-policy small">
          {c.policy_number && <span className="mono">{c.policy_number}</span>}
          {c.premium !== null && <span>{money(c.premium)}</span>}
          {c.renewal_date && <span>Renews <RenewalText date={c.renewal_date} /></span>}
        </div>
      )}
      {!!c.latest?.placements.length && <div className="ccard-insurers small"><Placements c={c} /></div>}
      <div className="ccard-stats">
        <div><b>{c.assessments}</b><span>{c.assessments === 1 ? 'assessment' : 'assessments'}</span></div>
        <div><b>{s ? `${Math.round(s.evidence_confidence * 100)}%` : '–'}</b><span>verified</span></div>
        <div><b className={s?.exceptions ? 'bad-t' : undefined}>{s ? s.exceptions : '–'}</b><span>exceptions</span></div>
      </div>
      <div className="ccard-foot">
        {c.latest ? <a href={href.score(c.latest.id)}>{c.latest.title}</a> : <span className="muted">No assessment yet</span>}
        {c.latest && <StatusPill status={c.latest.status} />}
        <span>{when(c.updated_at)}</span>
      </div>
    </article>
  ); // prettier-ignore
}

/** Where the client stands with each insurer, linking to its insurers page (task 93). */
function Placements({ c }: { c: ClientRow }) {
  const ps = c.latest?.placements ?? [];
  if (!ps.length || !c.latest) return <span className="muted">–</span>;
  return (
    <a
      className="placements"
      href={href.insurers(c.latest.id)}
      aria-label={`Insurers for ${c.customer_name}`}
    >
      {ps.map((p) => (
        <span key={p.form_key} className="placement">
          {p.insurer.split(' ')[0]} <PlacementPill status={p.status} />
        </span>
      ))}
    </a>
  );
}
